// Shared machinery for deciding WHEN a detected update may be applied, and
// for applying it safely. checkForUpdate.js owns the detection and the
// triggers; this file owns "is right now a moment we're allowed to yank the
// page out from under the player."
//
// Matt: "All of our apps need the new version auto reload feature we have in
// Cinemaroll." Ported from that repo's src/utils/appUpdate.js, with the
// unsafe-moment rules rewritten for this app - Cinema Roll's version only
// had to avoid interrupting a form or a mini-game round, whereas here the
// WHOLE app is a game with real sessions people care about.

// Waits until no service worker install is in flight, so a reload lands on
// the NEW app instead of a mixed old/new state. This app DOES register a
// worker (main.js, production builds only - workbox GenerateSW via
// vue.config.js, skipWaiting + clientsClaim), so this is a real wait here,
// not a formality. Capped; failures never block the reload.
export async function waitForNewWorker(timeoutMs = 15000) {
  try {
    const registration = await navigator.serviceWorker?.getRegistration?.();
    if (!registration) return;
    await registration.update().catch(() => {});
    const deadline = Date.now() + timeoutMs;
    while ((registration.installing || registration.waiting) && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop -- deliberate polling, not a batchable operation.
      await new Promise((resolve) => { setTimeout(resolve, 250); });
    }
  } catch {
    // Any surprise here must never eat the reload itself.
  }
}

export async function reloadForUpdate() {
  await waitForNewWorker();
  window.location.reload();
}

// createWebHashHistory (router/index.js) - every route is a `#/...` fragment
// of one document, so the "current route" for reload-safety purposes is
// readable straight off the URL without importing the router (this module
// stays free of Vue/Vuex so it can be unit-tested as plain functions).
export function currentRoutePath(href = window.location.href) {
  const hashIndex = href.indexOf('#');
  if (hashIndex === -1) return '/';
  const hash = href.slice(hashIndex + 1);
  const path = hash.split('?')[0];
  return path.startsWith('/') ? path : `/${path}`;
}

// A game is only "finished" once a winner is locked in. gameEndTriggered
// alone is NOT enough - that just means the final round is being played out
// (rulebook p16's equal-turns finish), which is still live play.
export function isGameFinished(gameState) {
  const meta = gameState?.meta;
  if (!meta) return false;
  return Boolean(meta.instantWinnerId || (meta.winnerIds && meta.winnerIds.length));
}

// Anything on screen that means the player is mid-something. There's no
// body-level scroll lock in this app (Cinema Roll's `no-scroll` hook has no
// equivalent here) - every overlay is a Teleport-to-body element, so the
// presence of its own root class is the signal.
const BLOCKING_OVERLAY_SELECTORS = [
  '.bug-report-backdrop', // the bug-report form, mid-typing or mid-send
  '.bug-resolution-backdrop', // reading what happened to a bug they reported
  '.glossary-popup-backdrop', // a glossary explanation is open
  '.glossary-banner', // glossary mode is ON - taps mean something different
  '.card-magnify', // TapMagnifyOverlay - reading a blown-up card
  '.install-prompt-backdrop', // the iOS "Add to Home Screen" instructions
  '.tutorial-callout', // TutorialOverlay (belt-and-braces with tutorialActive)
  '.roll-dice-popover', // dice mid-roll, or shown while a split is chosen
  '.admin-panel__list', // an admin is part-way through approving people
];

/**
 * Is RIGHT NOW a safe moment to reload out from under the player?
 * Pure-ish and fully injectable so the rules can be unit-tested directly.
 *
 * What survives a reload here, and what doesn't - this is what the rules
 * below are built on:
 *  - A LOCAL game is written to localStorage after every single action
 *    (store/localGameStorage.js via submitAction's persist()), and main.js
 *    dispatches loadLocalGame on boot, so the game itself comes back.
 *  - A REMOTE game lives in Firebase; the route rebinds on boot.
 *  - A TUTORIAL run is deliberately NOT persisted at all (startTutorial
 *    skips saveLocalGame on purpose - "a tutorial run isn't meant to
 *    survive a refresh"), so a reload destroys it outright.
 *  - `undoState`, and every bit of half-made choice living in GameBoard's
 *    own component data, are in memory only and are simply gone.
 *  - Worst of all: driveAITurns() is only ever kicked off by startLocalGame
 *    or submitAction. Nothing re-drives it on load. Reload while an AI turn
 *    sequence is in flight - and they run LONG, 500ms per step - and the
 *    restored game comes back with an AI as the active player and nothing
 *    on earth about to move it. That's a permanently stuck game, not an
 *    inconvenience.
 *
 * So: being ON a game screen with a live game is never a safe moment, even
 * after the 25s quiet stretch. Twenty-five seconds of no input is what
 * thinking about your board looks like, and it's also exactly what a long
 * AI turn looks like. Sitting on the landing/lobby screens IS safe - a
 * saved game is on disk there, not in play, so it survives fine (and
 * treating it as unsafe would mean anyone with an unfinished game never
 * got an update again).
 */
export function isSafeMomentForReload({
  activeElement = typeof document === 'undefined' ? null : document.activeElement,
  root = typeof document === 'undefined' ? null : document,
  routePath = currentRoutePath(),
  gameState = null,
  tutorialActive = false,
} = {}) {
  const tag = activeElement?.tagName || '';
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return false;
  if (activeElement?.isContentEditable) return false;

  if (root && BLOCKING_OVERLAY_SELECTORS.some((selector) => root.querySelector(selector))) return false;

  // A tutorial run evaporates on reload wherever it's launched from.
  if (tutorialActive) return false;

  // On a game screen with a game that hasn't been won yet: never.
  if (routePath.startsWith('/game') && gameState && !isGameFinished(gameState)) return false;

  return true;
}

// One auto-attempt per detected target bundle, ever - if the reload doesn't
// actually get us onto the new version (stuck worker, cache oddity), we stop
// rather than reloading in a loop. The manual reload button on the board's
// version tag remains the fallback.
const ATTEMPT_KEY = 'space-base:auto-update-attempted-for';

export function shouldAutoAttempt(targetBundle, storage = window.sessionStorage) {
  try {
    if (storage.getItem(ATTEMPT_KEY) === targetBundle) return false;
    storage.setItem(ATTEMPT_KEY, targetBundle);
    return true;
  } catch {
    return true; // storage unavailable: still better to try once than never
  }
}
